import React, { useEffect, useRef, useState, useMemo } from "react";
import { IonIcon, IonPage } from "@ionic/react";
import { useHistory, useLocation } from "react-router-dom";
import { Button, Card, Statistic, Progress, Typography, Space, Row, Col, Table, notification } from "antd";
import { PlusOutlined } from "@ant-design/icons";

import log from "../../../utils/log";
import c from "../../../utils/common";
import metaData from "../../../data/metaData";

import ActionHeader from "../../../components/Table/ActionHeader";
import DelaySearch from "../../../components/Table/DelaySearch";

import useAuthStore from "../../../states/useAuth";
import Meta from "../../../components/Meta";
import { formatTime } from "../../../utils/time";
import useWelcomeQuery from "../../../hooks/useQuery/useWelcome";
import SectionHeader from "../../../components/GetStarted/SectionHeader";
import { checkmarkCircleOutline } from "ionicons/icons";
import WelcomeStatus from "../../../components/Table/WelcomeStatus";

const { Title, Text } = Typography;

const Welcome = () => {
  const history = useHistory();
  const location = useLocation();
  const { auth, user } = useAuthStore();

  const welcomeQueryKey = useMemo(() => ["/welcome"], []);
  const { data = [], isFetching, error: welcomeError, refetch } = useWelcomeQuery("welcomes/query", welcomeQueryKey);
  log.debug(data);

  // State
  const [collapsed, setCollapsed] = useState(false);
  const [searchValue, setSearchValue] = useState();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [pagination, setPagination] = useState(1);
  const [filteredDataCount, setFilteredDataCount] = useState(data.length);
  const pageRef = useRef();

  // Error Notification
  useEffect(() => {
    if (welcomeError) {
      notification.error({ message: "Failed to fetch welcomes" });
    }
  }, [welcomeError]);

  // Memoized Filtered Data
  const filteredData = useMemo(() => {
    let filtered = data.filter((item) => {
      // Include search filtering if needed
      if (searchValue) {
        return (
          item.name?.toLowerCase().includes(searchValue.toLowerCase())
        );
      }
      return true;
    });

    return filtered.sort((a, b) => {
      return new Date(b.time.start) - new Date(a.time.start);
    });
  }, [data, searchValue]);

  // Update filteredDataCount separately when filteredData changes
  useEffect(() => {
    setFilteredDataCount(filteredData.length);
  }, [filteredData]);

  // Table Columns
  const columns = useMemo(
    () => [
      {
        title: "Name",
        dataIndex: "name",
        key: "name",
        width: 350,
        render: (text, record) => (
          <SectionHeader
            title={
              <Title level={5} style={{ margin: 0 }}>
                {record?.name}
              </Title>
            }
            description={record?.description}
          />
        ),
        sorter: (a, b) => (a?.name).localeCompare(b?.name),
      },
      {
        title: "Period",
        dataIndex: "time",
        key: "time",
        width: 300,
        render: (text, record) =>
          !record?.time ? null : (
            <Row style={{ width: "100%" }}>
              <Col span={24}>
                <WelcomeStatus welcome={record} />
              </Col>
              <Col span={3}>
                <Text strong>Start:</Text>
              </Col>
              <Col span={21}>
                <Text>{`${formatTime(record.time.start, "YYYY-MM-DD")}`}</Text>
              </Col>
              <br />
              <Col span={3}>
                <Text strong>End: </Text>
              </Col>
              <Col span={21}>
                <Text>{`${formatTime(record.time.end, "YYYY-MM-DD")}`}</Text>
              </Col>
            </Row>
          ),
      },
      {
        title: "Action",
        key: "action",
        width: 150,
        render: (text, record) => (
          <Space style={{ fontSize: "1rem" }}>
            <a onClick={() => history.push(`/merchantUser/welcome/${record?.id}`)}>detail</a>
          </Space>
        ),
      },
    ],
    [history]
  );

  // Pagination Handler
  const handleTableChange = (pagination) => {
    setPagination(pagination.current);
    scrollToTop();
  };

  // Scroll to Top
  const scrollToTop = () => {
    if (pageRef.current) {
      pageRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <div style={{ margin: "0 auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Title level={3}>Welcome Campaign</Title>
        <Button
          size="large"
          type="primary"
          icon={<PlusOutlined />}
          style={{ marginRight: "8px" }}
          onClick={() => history.push("/merchantUser/welcome/new")}
        >
          Add
        </Button>
      </div>

      <ActionHeader
        pagination={pagination}
        pageSize={20}
        selectedRowKeys={selectedRowKeys}
        setSelectedRowKeys={setSelectedRowKeys}
        count={filteredDataCount}
        onActionClick={log.debug}
      />

      <Table
        className="white-table-header"
        columns={columns}
        loading={isFetching}
        dataSource={filteredData}
        pagination={{
          current: pagination,
          pageSize: 20,
          position: ["none", "bottomCenter"],
          showSizeChanger: false,
        }}
        onChange={handleTableChange}
      />
    </div>
  );
};

export default Welcome;
